'use client'

import {
  cn,
  AnalysisFindingsCards,
  MatchQualityBadge,
  ScoreRing,
  resolveAnalysisRequirements,
  summarizeRequirements,
  Button,
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  PUBLIC_MATCH_QUALITY_LABELS,
  publicQualityFromEmployerScore,
} from '@yeshuman/sdk'
import type { EvaluationAnalysisPayload } from '@yeshuman/sdk'
import { Link } from 'react-router-dom'
import { SquareArrowOutUpRight } from 'lucide-react'
import type { JobWorkspacePersonLocationState } from '../../../lib/job-workspace-person-paths'

const ESTIMATE_NOTE =
  'Estimate only — full metrics and rationale appear after they join the pipeline and the next analysis run.'

export type CandidateVerdictSectionProps = {
  evaluationId?: string | null
  isProspect?: boolean
  className?: string
  payload?: EvaluationAnalysisPayload | null
  isLoading?: boolean
  isMissing?: boolean
  /** Score 0–1 for the ring (applicant match_score or payload). */
  matchScore?: number | null
  isEstimate?: boolean
  reviewTo?: string
  reviewState?: JobWorkspacePersonLocationState
  reviewLabel?: string
}

function headlineAddsDetail(headline: string, score: number): boolean {
  const bandLabel = PUBLIC_MATCH_QUALITY_LABELS[publicQualityFromEmployerScore(score)]
  const fold = headline.trim().toLowerCase()
  return fold.length > 0 && fold !== bandLabel.toLowerCase()
}

function VerdictScoreHeader({
  score,
  isEstimate,
  reviewTo,
  reviewState,
  reviewLabel,
}: {
  score: number
  isEstimate?: boolean
  reviewTo?: string
  reviewState?: JobWorkspacePersonLocationState
  reviewLabel: string
}) {
  return (
    <CardHeader className="p-0 pb-3">
      <CardTitle className="flex min-w-0 items-center gap-3 font-normal">
        <ScoreRing score={score} size={56} caption="" className="shrink-0" />
        <MatchQualityBadge
          score={score}
          estimate={isEstimate}
          showPercent={false}
          className="min-w-0"
        />
      </CardTitle>
      {reviewTo ? (
        <CardAction className="self-center">
          <Button type="button" variant="default" size="sm" asChild>
            <Link to={reviewTo} state={reviewState}>
              <SquareArrowOutUpRight className="h-4 w-4" />
              {reviewLabel}
            </Link>
          </Button>
        </CardAction>
      ) : null}
    </CardHeader>
  )
}

export function CandidateVerdictSection({
  evaluationId,
  isProspect,
  className,
  payload,
  isLoading = false,
  isMissing = false,
  matchScore = null,
  isEstimate = false,
  reviewTo,
  reviewState,
  reviewLabel = 'Open full review',
}: CandidateVerdictSectionProps) {
  const scoreFromPayload = payload?.scoring_trace.final_score
  const score =
    typeof scoreFromPayload === 'number'
      ? scoreFromPayload
      : typeof matchScore === 'number'
        ? matchScore
        : null

  if (isProspect || !evaluationId) {
    return (
      <Card className={className}>
        {score != null ? (
          <VerdictScoreHeader
            score={score}
            isEstimate
            reviewTo={reviewTo}
            reviewState={reviewState}
            reviewLabel={reviewLabel}
          />
        ) : reviewTo ? (
          <CardHeader className="p-0 pb-3">
            <CardTitle className="text-sm font-semibold">Estimate</CardTitle>
            <CardAction className="self-center">
              <Button type="button" variant="default" size="sm" asChild>
                <Link to={reviewTo} state={reviewState}>
                  <SquareArrowOutUpRight className="h-4 w-4" />
                  {reviewLabel}
                </Link>
              </Button>
            </CardAction>
          </CardHeader>
        ) : null}
        <CardContent className={cn('p-0', (score != null || reviewTo) && 'pt-0')}>
          <p className="text-sm text-muted-foreground">{ESTIMATE_NOTE}</p>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card className={className}>
        {score != null ? (
          <VerdictScoreHeader
            score={score}
            isEstimate={isEstimate}
            reviewTo={reviewTo}
            reviewState={reviewState}
            reviewLabel={reviewLabel}
          />
        ) : null}
        <CardContent className="space-y-2 p-0">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </CardContent>
      </Card>
    )
  }

  if (isMissing) {
    return (
      <Card className={className}>
        {score != null ? (
          <VerdictScoreHeader
            score={score}
            isEstimate={isEstimate}
            reviewTo={reviewTo}
            reviewState={reviewState}
            reviewLabel={reviewLabel}
          />
        ) : null}
        <CardContent className="p-0">
          <p className="text-sm text-muted-foreground">Match details are not available yet.</p>
        </CardContent>
      </Card>
    )
  }

  if (!payload) {
    return null
  }

  const { requirements } = resolveAnalysisRequirements(payload)
  const { metCount, knownCount } = summarizeRequirements(requirements)
  const resolvedScore = score ?? 0
  const showHeadline = headlineAddsDetail(payload.headline, resolvedScore)

  return (
    <Card className={className}>
      <VerdictScoreHeader
        score={resolvedScore}
        isEstimate={isEstimate}
        reviewTo={reviewTo}
        reviewState={reviewState}
        reviewLabel={reviewLabel}
      />
      <CardContent className="space-y-3 p-0">
        {showHeadline ? (
          <p className="text-sm font-medium">{payload.headline}</p>
        ) : null}
        {requirements.length > 0 && knownCount > 0 ? (
          <p className="text-xs text-muted-foreground">
            {metCount} of {knownCount} must-haves met
          </p>
        ) : null}
        <AnalysisFindingsCards
          highlights={payload.findings.highlights}
          redFlags={payload.findings.red_flags}
          maxHighlights={3}
          maxRedFlags={2}
        />
      </CardContent>
    </Card>
  )
}
